document.addEventListener("DOMContentLoaded", () => {
    // =============================================
    // 1. STATE MANAGEMENT
    // =============================================
    const state = {
        uploadedImage: null,
        background_color: "White",
        text_font: "Anton",
        text_color: "Black",
        custom_text1: "",
        custom_text2: "",
        custom_text3: "",
        quantity: 2,
        price: 27.99
    };

    const DEFAULT_DROPDOWN_VALUES = {
        background_color: "White",
        text_font: "Anton",
        quantity: "2"
    };

    const ALLOWED_UPLOAD_EXT = [
        "jpg",
        "jpeg",
        "png",
        "webp",
        "svg",
        "pdf",
        "tiff",
        "tif",
        "ai",
        "eps",
    ];

    const ALLOWED_UPLOAD_MIME = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
        "image/svg+xml",
        "image/tiff",
        "application/pdf",
        "application/postscript",
        "application/illustrator",
        "application/vnd.adobe.illustrator",
    ];

    const NO_PREVIEW_EXT = ["ai", "eps"];

    // =============================================
    // 2. PRICE VARIANTS
    // =============================================
    const PRICE_VARIANTS = {
        quantity: [
            { value: "2", price: 0 },
            { value: "4", price: 25.00 },
            { value: "10", price: 70.01 },
            { value: "20", price: 150.01 },
            { value: "30", price: 209.72 },
            { value: "40", price: 249.20 },
            { value: "50", price: 269.52 },
        ],
    };

    // =============================================
    // 3. HELPER FUNCTIONS
    // =============================================
    function show(el) {
        el.style.display = "";
    }

    function hide(el) {
        el.style.display = "none";
    }

    function animatePriceChange(targetElement, start, end, duration = 500) {
        let startTime = null;

        function step(timestamp) {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            const currentValue = start + (end - start) * progress;
            targetElement.textContent = currentValue.toFixed(2);
            if (progress < 1) requestAnimationFrame(step);
        }

        requestAnimationFrame(step);
    }

    function priceOf(groupKey, chosenValue) {
        if (!chosenValue) return 0;
        const list = PRICE_VARIANTS[groupKey] || [];
        const found = list.find(item => item.value === chosenValue);
        return found ? found.price : 0;
    }

    const PCYS_CART_BTN_HTML = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="20" r="1.5"/><circle cx="18" cy="20" r="1.5"/><path d="M2 3h3l2.4 12.4a2 2 0 0 0 2 1.6h8.2a2 2 0 0 0 2-1.6L23 6H6"/></svg> Add to cart`;
    const PCYS_CART_LOADING_HTML = `<span class="pcys-cart-loading"><svg class="pcys-cart-loading__icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-6.22-8.56"/></svg>Adding to cart</span>`;

    function formatUploadFileName(name, maxBaseLen = 22) {
        if (!name) return "";
        const lastDot = name.lastIndexOf(".");
        if (lastDot <= 0) return name;
        const base = name.slice(0, lastDot);
        const ext = name.slice(lastDot);
        return base.length <= maxBaseLen ? name : `${base.slice(0, maxBaseLen)}...${ext}`;
    }

    function getFileExtension(file) {
        const parts = file.name.split(".");
        return parts.length > 1 ? parts.pop().toLowerCase() : "";
    }

    function isAllowedUpload(file) {
        const ext = getFileExtension(file);
        if (ALLOWED_UPLOAD_EXT.includes(ext)) return true;
        if (file.type && ALLOWED_UPLOAD_MIME.includes(file.type)) return true;
        return false;
    }

    function readFileAsDataURL(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error("Error reading file"));
            reader.readAsDataURL(file);
        });
    }

    let pdfJsLoading = null;
    function loadPdfJs() {
        if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
        if (pdfJsLoading) return pdfJsLoading;
        pdfJsLoading = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src =
                "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
            script.onload = () => {
                window.pdfjsLib.GlobalWorkerOptions.workerSrc =
                    "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
                resolve(window.pdfjsLib);
            };
            script.onerror = () =>
                reject(new Error("Could not load PDF preview library"));
            document.head.appendChild(script);
        });
        return pdfJsLoading;
    }

    async function renderPdfFirstPagePreview(file) {
        const pdfjsLib = await loadPdfJs();
        const data = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data }).promise;
        const page = await pdf.getPage(1);
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = Math.min(
            256 / baseViewport.width,
            256 / baseViewport.height,
            2
        );
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({
            canvasContext: canvas.getContext("2d"),
            viewport,
        }).promise;
        return canvas.toDataURL("image/jpeg", 0.88);
    }

    async function buildUploadPreview(file) {
        const ext = getFileExtension(file);
        if (NO_PREVIEW_EXT.includes(ext)) {
            return { src: null, placeholder: true, widthText: "", heightText: "" };
        }
        if (ext === "pdf" || file.type === "application/pdf") {
            try {
                const src = await renderPdfFirstPagePreview(file);
                return { src, placeholder: false, widthText: "", heightText: "" };
            } catch (err) {
                console.warn("PDF preview failed:", err);
                return { src: null, placeholder: true, widthText: "", heightText: "" };
            }
        }
        if (
            file.type.startsWith("image/") ||
            ["jpg", "jpeg", "png", "webp", "svg", "tiff", "tif"].includes(ext)
        ) {
            const src = await readFileAsDataURL(file);
            const dimensions = await new Promise((resolve) => {
                const probe = new Image();
                probe.onload = () => {
                    const dpi = 72;
                    resolve({
                        widthText: `${(probe.naturalWidth / dpi).toFixed(2)}"`,
                        heightText: `${(probe.naturalHeight / dpi).toFixed(2)}"`,
                    });
                };
                probe.onerror = () => resolve({ widthText: "", heightText: "" });
                probe.src = src;
            });
            return { src, placeholder: false, ...dimensions };
        }
        return { src: null, placeholder: true, widthText: "", heightText: "" };
    }

    function setUploadPreview(src, usePlaceholder, widthText = "", heightText = "") {
        const thumb = document.getElementById("upload-preview-thumb");
        const placeholder = document.getElementById("upload-preview-placeholder");
        const previewImage = document.getElementById("preview-image");
        const widthEl = document.getElementById("width-inch");
        const heightEl = document.getElementById("height-inch");

        if (thumb) {
            if (src && !usePlaceholder) {
                thumb.src = src;
                thumb.hidden = false;
            } else {
                thumb.hidden = true;
                thumb.removeAttribute("src");
            }
        }

        if (placeholder) placeholder.hidden = !usePlaceholder;
        if (previewImage) previewImage.src = src || "";
        if (widthEl) widthEl.textContent = widthText;
        if (heightEl) heightEl.textContent = heightText;
    }

    function setUploadUI(hasFile, fileName) {
        const uploadZone = document.querySelector(".pcys-upload-zone");
        const previewBox = document.getElementById("preview-box");
        const successBar = document.getElementById("upload-success-bar");
        const filenameEl = document.getElementById("upload-filename");

        if (uploadZone) {
            uploadZone.classList.toggle("pcys-upload-zone--hidden", hasFile);
        }
        if (previewBox) {
            previewBox.classList.toggle("pcys-upload-success--visible", hasFile);
            previewBox.style.display = hasFile ? "block" : "none";
        }
        if (successBar) {
            successBar.hidden = !hasFile;
        }
        if (filenameEl) {
            if (fileName) {
                filenameEl.textContent = formatUploadFileName(fileName);
                filenameEl.title = fileName;
            } else {
                filenameEl.textContent = "design.png";
                filenameEl.removeAttribute("title");
            }
        }
        if (!hasFile) setUploadPreview(null, false);
    }

    // =============================================
    // 4. IMAGE UPLOAD HANDLING
    // =============================================
    function setupImageUpload() {
        const fileInput = document.getElementById("upload-file");
        const chooseFileBtn = document.getElementById("choose-file-btn");
        const deleteImage = document.getElementById("delete-image");
        const errorMessage = document.getElementById("upload-file-error");
        const dropZone = document.querySelector(".pcys-upload-zone");

        // Clean up any existing event listeners
        const freshChooseBtn = chooseFileBtn.cloneNode(true);
        const freshFileInput = fileInput.cloneNode(true);
        chooseFileBtn.replaceWith(freshChooseBtn);
        fileInput.replaceWith(freshFileInput);

        // New click handler
        freshChooseBtn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            freshFileInput.click();
        });

        if (dropZone) {
            ["dragenter", "dragover"].forEach((evt) => {
                dropZone.addEventListener(evt, (e) => {
                    e.preventDefault();
                    dropZone.classList.add("is-dragover");
                });
            });
            ["dragleave", "drop"].forEach((evt) => {
                dropZone.addEventListener(evt, (e) => {
                    e.preventDefault();
                    dropZone.classList.remove("is-dragover");
                });
            });
            dropZone.addEventListener("drop", (e) => {
                const file = e.dataTransfer?.files?.[0];
                if (!file) return;
                const dt = new DataTransfer();
                dt.items.add(file);
                freshFileInput.files = dt.files;
                freshFileInput.dispatchEvent(new Event("change", { bubbles: true }));
            });
        }

        freshFileInput.addEventListener("change", async function (event) {
            const file = event.target.files[0];
            if (!file) return;

            if (!isAllowedUpload(file)) {
                errorMessage.textContent =
                    "Please upload a valid file (.jpg, .png, .webp, .pdf, .svg, .ai, .eps)";
                errorMessage.style.display = "block";
                state.uploadedImage = null;
                setUploadPreview(null, false);
                setUploadUI(false);
                return;
            }

            try {
                const preview = await buildUploadPreview(file);
                setUploadPreview(
                    preview.src,
                    preview.placeholder,
                    preview.widthText,
                    preview.heightText
                );
                setUploadUI(true, file.name);
                errorMessage.style.display = "none";
                state.uploadedImage = file;
            } catch (err) {
                console.error(err);
                errorMessage.textContent = "Error reading file. Please try again.";
                errorMessage.style.display = "block";
                state.uploadedImage = null;
                setUploadPreview(null, false);
                setUploadUI(false);
            }
        });

        deleteImage.addEventListener("click", function (e) {
            e.preventDefault();
            e.stopPropagation();
            freshFileInput.value = "";
            setUploadUI(false);
            state.uploadedImage = null;
            errorMessage.style.display = "none";
        });
    }

    // =============================================
    // 5. DROPDOWN HANDLING
    // =============================================
    function initDropdownDefaults() {
        document.querySelectorAll(".select-dropdown").forEach(dd => {
            // If an <li data-value> is pre-selected via .selected, copy to header
            const preSel = dd.querySelector("li.selected");
            if (preSel) {
                dd.querySelector(".selected-text").textContent = preSel.textContent;
            }
        });
    }

    function dropdownClickHandler(evt) {
        /* A. find the dropdown wrapper (if any) */
        const dropdown = evt.target.closest(".select-dropdown");

        /* B. click happened outside every dropdown → close all & exit */
        if (!dropdown) {
            document.querySelectorAll(".dropdown-options").forEach(list => list.style.display = "none");
            return;
        }

        const list = dropdown.querySelector(".dropdown-options");
        const header = dropdown.querySelector(".selected-text");
        const stateKey = dropdown.dataset.stateKey;
        const optionEl = evt.target.closest("li[data-value]");

        /* C. close every other open list, keep this one intact */
        document.querySelectorAll(".dropdown-options").forEach(other => {
            if (other !== list) other.style.display = "none";
        });

        /* D. user clicked an <li> option */
        if (optionEl) {
            const value = optionEl.dataset.value;

            list.querySelectorAll("li").forEach(li => li.classList.remove("selected"));
            optionEl.classList.add("selected");
            header.textContent = optionEl.textContent;
            dropdown.classList.add("variant-picked");

            if (stateKey === "text_font") {
                header.classList.forEach((cls) => {
                    if (cls.startsWith('font-')) header.classList.remove(cls);
                });
                optionEl.classList.forEach((cls) => {
                    if (cls.startsWith('font-')) header.classList.add(cls);
                });
            }

            if (stateKey) state[stateKey] = value; // sync shared state

            list.style.display = "none"; // close THIS list
            recalcPrice();
            return;
        }

        /* E. user clicked header/arrow → toggle this list */
        list.style.display = list.style.display === "block" ? "none" : "block";
    }

    // =============================================
    // 6. TEXT COLOR SELECTION
    // =============================================
    function setupTextColorSelection() {
        const swatchContainer = document.querySelector(".sticker-variants__list.letter-color-variants__list");
        const selectedColor = document.getElementById("selected-text-color");

        swatchContainer.addEventListener("click", (event) => {
            const clickedSwatch = event.target.closest(".sticker-variant");
            if (!clickedSwatch) return;
            if (clickedSwatch.classList.contains("active")) return;

            const previousActive = swatchContainer.querySelector(".sticker-variant.active");

            if (previousActive) previousActive.classList.remove("active");
            clickedSwatch.classList.add("active");
            selectedColor.textContent = clickedSwatch.dataset.color;
            state.text_color = clickedSwatch.dataset.color;
        });
    }

    // =============================================
    // 7. CUSTOM TEXT INPUT HANDLING
    // =============================================
    function handleAllCustomTextInputs() {
        const inputs = [
            { id: "custom-text1-input", counterId: "custom-text1-char-count", key: "custom_text1" },
            { id: "custom-text2-input", counterId: "custom-text2-char-count", key: "custom_text2" },
            { id: "custom-text3-input", counterId: "custom-text3-char-count", key: "custom_text3" }
        ];

        inputs.forEach(({ id, counterId, key }) => {
            const input = document.getElementById(id);
            const counter = document.getElementById(counterId);
            const MAX = 200;

            const refresh = () => {
                if (input.value.length > MAX) input.value = input.value.slice(0, MAX);
                state[key] = input.value;
                counter.textContent = `${MAX - input.value.length} character(s) left`;
            };

            refresh();
            input.addEventListener("input", refresh);
        });
    }




    // =============================================
    // 10. PRICE CALCULATION
    // =============================================
    function recalcPrice() {
        let total = 27.99; // Base price

        // Add for quantity
        const quantityPrice = priceOf("quantity", String(state.quantity));
        total += quantityPrice;


        // Update display
        const priceEl = document.getElementById("pcs-final-price");
        const prevValue = parseFloat(priceEl.textContent) || 0;
        if (prevValue !== total) {
            animatePriceChange(priceEl, prevValue, total);
        }
        state.price = total;
    }

    // =============================================
    // 11. FORM VALIDATION
    // =============================================
    function validateForm() {
        let firstInvalid = null;

        function toggleError(id, show, message = "") {
            const bar = document.getElementById(id);
            if (!bar) return;

            if (message) bar.textContent = message;
            bar.style.display = show ? "block" : "none";

            const variantBox = bar.closest(".pcs-variant");
            if (variantBox) variantBox.classList.toggle("has-error", show);

            if (show && !firstInvalid) firstInvalid = variantBox || bar;
        }

        // Validate uploaded image
        const fileInput = document.getElementById("upload-file");
        state.uploadedImage = fileInput.files[0] || null;
        // toggleError(
        //     "upload-file-error",
        //     !state.uploadedImage,
        //     "Please upload a valid image file (.jpg, .png, .pdf, .svg)"
        // );


        // Validate quantity
        toggleError("quantity-error-bar", !state.quantity);

        if (firstInvalid) {
            firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
            return false;
        }
        return true;
    }

    // =============================================
    // 12. RESET FORM
    // =============================================
    function resetCalculatorForm() {
        // Clear uploaded image
        const fileInput = document.getElementById("upload-file");
        if (fileInput) fileInput.value = "";
        state.uploadedImage = null;
        setUploadUI(false);

        // Reset dropdowns - updated to properly reset both display and value
        document.querySelectorAll(".select-dropdown").forEach(dd => {
            const list = dd.querySelector(".dropdown-options");
            const header = dd.querySelector(".selected-text");
            const firstOption = list.querySelector("li[data-value]");
            const stateKey = dd.dataset.stateKey;
            const defaultValue = DEFAULT_DROPDOWN_VALUES[stateKey];
            const defaultOption = defaultValue
                ? list.querySelector(`li[data-value="${defaultValue}"]`)
                : null;

            // Remove selected class from all options
            list.querySelectorAll("li").forEach(li => li.classList.remove("selected"));

            const resetOption = defaultOption || firstOption;

            if (resetOption) {
                resetOption.classList.add("selected");
                header.textContent = resetOption.textContent.trim();

                if (stateKey) {
                    state[stateKey] = resetOption.dataset.value;
                }
            }

            // Close all dropdowns
            list.style.display = "none";
            dd.classList.remove("variant-picked");
        });

        // Reset text color
        const colorSwatches = document.querySelector(".letter-color-variants__list");
        if (colorSwatches) {
            const firstSwatch = colorSwatches.querySelector(".sticker-variant");
            colorSwatches.querySelectorAll(".sticker-variant").forEach(el => {
                el.classList.remove("active");
            });
            if (firstSwatch) {
                firstSwatch.classList.add("active");
                state.text_color = firstSwatch.dataset.color;
                document.getElementById("selected-text-color").textContent = firstSwatch.dataset.color;
            }
        }

        // Reset custom text
        ["custom_text1", "custom_text2", "custom_text3"].forEach((key, index) => {
            const input = document.getElementById(`custom-text${index + 1}-input`);
            const counter = document.getElementById(`custom-text${index + 1}-char-count`);
            if (input) input.value = "";
            if (counter) counter.textContent = "200 character(s) left";
            state[key] = "";
        });



        // Reset state to default values
        state.background_color = "White";
        state.text_font = "Anton";
        state.quantity = 2;

        // Reset price
        recalcPrice();

        // Clear any error states
        document.querySelectorAll(".error-bar").forEach(el => {
            el.style.display = "none";
        });
        document.querySelectorAll(".pcs-variant").forEach(el => {
            el.classList.remove("has-error");
        });
    }

    // =============================================
    // 13. ADD TO CART FUNCTIONALITY
    // =============================================
    function setupApippaAddToCart() {
        const addToCartBtn = document.getElementById("pcys-add-to-cart");
        if (!addToCartBtn) return;

        addToCartBtn.addEventListener("click", async (e) => {
            e.preventDefault();

            /* ──────── A. CLIENT-SIDE VALIDATION ──────── */
            if (!validateForm()) {
                console.log("Form invalid – fix the highlighted fields.");
                return; // ⟵ stop everything
            }
            console.log("Form valid – adding to cart:", state);

            /* ──────── B. UI: show loader ──────── */
            addToCartBtn.disabled = true;
            addToCartBtn.classList.add("loading");
            addToCartBtn.innerHTML = PCYS_CART_LOADING_HTML;




            try {
                /* ──────── C. Build Apippa payload ──────── */
                const requestData = {
                    calculatorId: "9b905b6c8f394", // Replace with your actual calculator ID
                    productId: 7705284739120, // Replace with your actual product ID
                    properties: {
                        "Background Color": state.background_color,
                        "Text Font": state.text_font,
                        "Text Color": state.text_color,
                        "Enter Text 1": state.custom_text1,
                        "Enter Text 2": state.custom_text2,
                        "Enter Text 3": state.custom_text3,
                        "Signs": state.quantity,
                        "Upload Image": state.uploadedImage?.name || "",
                    }
                };

                /* D. Create / fetch the variant via Apippa */
                const variantRes = await fetch(
                    "https://api.custompricecalculator.com/api/v1/variant",
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(requestData),
                    }
                );

                const variantId = await variantRes.json();
                console.log("Variant Id: ", variantId);
                const shopifyVariantId =
                    typeof variantId === "string"
                        ? variantId
                        : variantId?.variantId || variantId?.id;

                if (!shopifyVariantId) throw new Error("Invalid variant ID");

                /* E. Add that variant to the Shopify cart */
                const formData = new FormData();
                formData.append("id", shopifyVariantId);
                formData.append("quantity", 1);

                // Add properties
                Object.entries(requestData.properties).forEach(([key, value]) => {
                    formData.append(`properties[${key}]`, value);
                });

                // Add image file if uploaded
                if (state.uploadedImage) {
                    formData.append("properties[Upload Image]", state.uploadedImage);
                }

                // Add to cart with proper file handling
                const cartAddRes = await fetch("/cart/add.js", {
                    method: "POST",
                    body: formData,
                });

                const cartAddJson = await cartAddRes.json(); // Parse response JSON
                console.log("Add to Cart Response:", cartAddJson);

                localStorage.setItem(
                    `patched-cart-item-${cartAddJson.id}`,
                    JSON.stringify({
                        id: cartAddJson.id,
                        title: cartAddJson.product_title,
                        image: cartAddJson.image,
                        price: cartAddJson.price,
                    })
                );

                if (!cartAddRes.ok) throw new Error("Failed to add to cart");

                const cartJson = {
                    items: [cartAddJson],
                    item_count: 1,
                    total_price: cartAddJson.final_price,
                    original_total_price: cartAddJson.original_price,
                    total_discount: 0,
                };

                // Get mini-cart HTML with cache busting
                const timestamp = new Date().getTime();
                await new Promise(resolve => setTimeout(resolve, 200)); // slight delay
                const sectionHtml = await fetch(
                    `/cart?section_id=mini-cart&t=${timestamp}`
                ).then(res => res.text());

                const fakeDiv = document.createElement("div");
                fakeDiv.innerHTML = sectionHtml;
                const miniCartHTML = fakeDiv.querySelector("cart-drawer")?.outerHTML;

                const cartDrawer = document.querySelector("cart-drawer");
                if (cartDrawer && miniCartHTML) {
                    cartDrawer.dispatchEvent(
                        new CustomEvent("cart:refresh", {
                            bubbles: true,
                            detail: {
                                cart: {
                                    ...cartJson,
                                    sections: {
                                        "mini-cart": miniCartHTML,
                                    },
                                },
                                openMiniCart: true,
                            },
                        })
                    );
                }

                // Additional verification after cart opens
                setTimeout(() => {
                    const lineItems = cartDrawer.querySelectorAll("line-item");

                    lineItems.forEach(lineItem => {
                        const desktopPriceContainer = lineItem.querySelector(
                            ".line-item__price-list-container .price"
                        );

                        const mobilePriceContainer = lineItem.querySelector(
                            ".product-item-meta__price-list-container .price-list.hidden-tablet-and-up .price"
                        );

                        if (!desktopPriceContainer && !mobilePriceContainer) return;

                        const priceText = (
                            desktopPriceContainer || mobilePriceContainer
                        ).textContent.trim();
                        const isFree = /Free|\$0\.00/.test(priceText);

                        let variantId;
                        const productLink = lineItem.querySelector("a[href*='?variant=']");
                        if (productLink?.href.includes("variant=")) {
                            variantId = productLink.href.split("variant=")[1].split("&")[0];
                        }

                        if (!variantId) {
                            const removeBtn = lineItem.querySelector(
                                "a[href*='/cart/change?id=']"
                            );
                            const removeHref = removeBtn?.getAttribute("href") || "";
                            const match = removeHref.match(/id=(\d+):/);
                            variantId = match?.[1];
                        }

                        if (isFree && variantId == shopifyVariantId) {
                            const matchingItem = cartJson.items.find(
                                item => String(item.id) === variantId
                            );

                            if (matchingItem) {
                                const correctPrice = (matchingItem.price / 100).toFixed(2);

                                const imageWrapper = lineItem.querySelector(
                                    ".line-item__image-wrapper"
                                );
                                const productThumbnail = imageWrapper.querySelector(
                                    "img.line-item__image"
                                );
                                const productTitle = lineItem.querySelector(
                                    ".product-item-meta .product-item-meta__title"
                                );

                                if (!productThumbnail) {
                                    const imgSrc = document.querySelector(
                                        ".product__thumbnail-item .product__thumbnail img"
                                    ).src;
                                    imageWrapper.innerHTML += `
                    <img src="${imgSrc}" alt="" srcset="${imgSrc}" width="1024" height="1024" loading="lazy" sizes="(max-width: 740px) 80px, 92px" class="line-item__image">
                  `;
                                }

                                if (productTitle.textContent.trim() === "") {
                                    productTitle.textContent = document.querySelector(
                                        ".product__info .product-meta__title"
                                    ).textContent;
                                }

                                // Update both prices
                                const updatedPriceHTML = `
                  <span class="visually-hidden">Sale price</span>
                  $${correctPrice}
                `;
                                if (desktopPriceContainer) {
                                    desktopPriceContainer.innerHTML = updatedPriceHTML;
                                }
                                if (mobilePriceContainer) {
                                    mobilePriceContainer.innerHTML = updatedPriceHTML;
                                }
                            }
                        }
                    });
                }, 300);

                setTimeout(async () => {
                    const cartJson = await fetch("/cart.js").then(res => res.json());
                    const cartCountEl = document.querySelector("cart-count");
                    if (cartCountEl) {
                        cartCountEl.textContent = cartJson.item_count;
                    }
                }, 300);

                setTimeout(() => {
                    const drawerContent = document.querySelector(
                        "#shopify-section-mini-cart .drawer__content"
                    );
                    if (!drawerContent) return;

                    const confetti = document.createElement("dotlottie-player");
                    confetti.className = "mini-cart-lottie";
                    confetti.src =
                        "//cdn.shopify.com/s/files/1/0582/5400/0176/files/confetti.json?v=1748024242";
                    confetti.setAttribute("background", "transparent");
                    confetti.setAttribute("speed", "1");
                    confetti.setAttribute("style", "width: 300px; height: 300px");
                    confetti.setAttribute("direction", "1");
                    confetti.setAttribute("playmode", "normal");
                    confetti.setAttribute("autoplay", "");

                    drawerContent.prepend(confetti);

                    setTimeout(() => {
                        confetti.remove();
                    }, 3000);
                }, 1000);
            } catch (err) {
                console.error("Apippa Add to Cart Error:", err);
                alert("There was a problem adding your item to the cart.");
            } finally {
                /* H. Restore button */
                addToCartBtn.disabled = false;
                addToCartBtn.classList.remove("loading");
                addToCartBtn.innerHTML = PCYS_CART_BTN_HTML;
                resetCalculatorForm();
            }
        });
    }

    // Text animation helpers
    function typeText(el, text, speed = 60) {
        return new Promise(resolve => {
            let i = 0;
            const typeInterval = setInterval(() => {
                el.textContent += text[i];
                i++;
                if (i >= text.length) {
                    clearInterval(typeInterval);
                    resolve();
                }
            }, speed);
        });
    }

    function deleteText(el, speed = 40) {
        return new Promise(resolve => {
            const delInterval = setInterval(() => {
                el.textContent = el.textContent.slice(0, -1);
                if (el.textContent.length === 0) {
                    clearInterval(delInterval);
                    resolve();
                }
            }, speed);
        });
    }

    // =============================================
    // 14. INITIALIZE EVERYTHING
    // =============================================
    function initialize() {
        setupImageUpload();
        initDropdownDefaults();
        setupTextColorSelection();
        handleAllCustomTextInputs();
        setupApippaAddToCart();
        document.addEventListener("click", dropdownClickHandler);
        const addToCartBtn = document.getElementById("pcys-add-to-cart");
        if (addToCartBtn) addToCartBtn.innerHTML = PCYS_CART_BTN_HTML;
        setUploadUI(false);
        recalcPrice(); // Initial price calculation
    }

    initialize();
});
